import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { TrendingUp, DollarSign, Repeat, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import TrialSignupDialog from "@/components/TrialSignupDialog";

function formatMoney(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

export default function Calculator() {
  const [appointments, setAppointments] = useState([320]);
  const [avgTicket, setAvgTicket] = useState([85]);
  const [rebookLift, setRebookLift] = useState([18]);
  const [reviewLift, setReviewLift] = useState([12]);
  const [trialOpen, setTrialOpen] = useState(false);

  const results = useMemo(() => {
    const monthlyAppts = appointments[0];
    const ticket = avgTicket[0];
    const rebookP = rebookLift[0] / 100;
    const reviewP = reviewLift[0] / 100;
    const rebookRevenue = monthlyAppts * rebookP * ticket;
    const reviewRevenue = monthlyAppts * reviewP * ticket * 0.6; // reviews drive new bookings at partial rate
    const monthly = rebookRevenue + reviewRevenue;
    const yearly = monthly * 12;
    return { monthly, yearly, rebookRevenue, reviewRevenue };
  }, [appointments, avgTicket, rebookLift, reviewLift]);

  return (
    <section id="calculator" className="py-24 sm:py-32 bg-[#183D36] text-[#FAF7F0] relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#F4B183]/15 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 -left-40 w-[500px] h-[500px] rounded-full bg-[#A8BFA3]/15 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="max-w-2xl mb-14">
          <div className="overline mb-4 text-[#F4B183]">Revenue calculator</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
            See what you're leaving on the table.
          </h2>
          <p className="mt-5 text-lg text-[#FAF7F0]/80 leading-relaxed">
            Move the sliders to match your business. This is a directional estimate based on typical rebooking and review-driven acquisition lift — not a guarantee.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-3 space-y-10">
            <SliderRow
              testId="calc-slider-appointments"
              label="Appointments per month"
              value={appointments}
              onChange={setAppointments}
              min={20}
              max={2000}
              step={10}
              display={appointments[0].toLocaleString()}
            />
            <SliderRow
              testId="calc-slider-ticket"
              label="Average ticket"
              value={avgTicket}
              onChange={setAvgTicket}
              min={20}
              max={500}
              step={5}
              display={formatMoney(avgTicket[0])}
            />
            <SliderRow
              testId="calc-slider-rebook"
              label="Rebooking lift with NextVisit"
              value={rebookLift}
              onChange={setRebookLift}
              min={5}
              max={40}
              step={1}
              display={`${rebookLift[0]}%`}
            />
            <SliderRow
              testId="calc-slider-review"
              label="New bookings from reviews"
              value={reviewLift}
              onChange={setReviewLift}
              min={2}
              max={30}
              step={1}
              display={`${reviewLift[0]}%`}
            />
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-3xl bg-[#FAF7F0] text-[#183D36] p-8 sm:p-10 shadow-2xl">
              <div className="overline mb-3">Estimated new revenue</div>
              <div className="font-heading text-5xl sm:text-6xl leading-none tracking-tight" data-testid="calc-monthly-value">
                {formatMoney(results.monthly)}
              </div>
              <div className="text-sm text-[#4A4A4A] mt-2">per month</div>

              <div className="mt-6 pt-6 border-t border-[#183D36]/10 grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-[#4A4A4A] font-accent uppercase tracking-widest">Yearly</div>
                  <div className="font-heading text-2xl mt-1" data-testid="calc-yearly-value">{formatMoney(results.yearly)}</div>
                </div>
                <div>
                  <div className="text-xs text-[#4A4A4A] font-accent uppercase tracking-widest">Rebookings</div>
                  <div className="font-heading text-2xl mt-1">{formatMoney(results.rebookRevenue)}</div>
                </div>
              </div>

              <div className="mt-6 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-[#183D36]">
                  <Repeat size={14} className="text-[#F4B183]" /> Rebooking nudges pay for themselves in week one
                </div>
                <div className="flex items-center gap-2 text-[#183D36]">
                  <Star size={14} className="text-[#F4B183]" /> Reviews turn into free acquisition
                </div>
              </div>

              <Button
                data-testid="calc-start-free-btn"
                onClick={() => setTrialOpen(true)}
                className="mt-8 w-full rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] py-6 text-base font-medium">
                Start free for 14 days
              </Button>
            </div>
          </div>
        </div>
      </div>

      <TrialSignupDialog open={trialOpen} onOpenChange={setTrialOpen} />
    </section>
  );
}

function SliderRow({ testId, label, value, onChange, min, max, step, display }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <label className="text-sm font-medium text-[#FAF7F0]/90">{label}</label>
        <span className="font-heading text-2xl text-[#F4B183]">{display}</span>
      </div>
      <div className="nv-slider">
        <Slider
          data-testid={testId}
          value={value}
          onValueChange={onChange}
          min={min}
          max={max}
          step={step}
        />
      </div>
    </div>
  );
}
